// @ts-nocheck -- Transitional typing, matching the surrounding deal modules.
import { BadgeCheck, Check, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Audience, ClientRelease } from "@/enum/common.enum";
import { cn } from "@/utils/common.utils";

/** Agent control for client-release gates.
 *
 * Both gated choices need a matching action/state in every agent task view.
 * Completion and scheduled-fact entry also confirm `after_confirmed` tasks on
 * the server; this explicit control covers real-world facts verified outside
 * Stoop without forcing the agent to mark the task complete.
 */
export default function ClientReleaseAction({ task, onRelease, onConfirm, working = false, className = "" }) {
  if (task.audience !== Audience.CLIENT) {
    return null;
  }

  if (task.client_release === ClientRelease.AFTER_CONFIRMED) {
    if (task.confirmed_at) {
      return (
        <span
          className={cn("inline-flex shrink-0 items-center gap-1 text-xs font-medium text-dash-greenDeep", className)}
          data-testid={`task-client-confirmed-${task.id}`}
        >
          <Check className="size-3.5" aria-hidden /> Confirmed for client
        </span>
      );
    }

    return (
      <Button
        type="button"
        variant="dashboardOutline"
        size="sm"
        className={cn("shrink-0", className)}
        onClick={() => onConfirm?.(task)}
        loading={working}
        disabled={working}
        aria-label={`Confirm ${task.title} happened`}
        data-testid={`task-confirm-client-${task.id}`}
      >
        <BadgeCheck data-icon="inline-start" /> Confirm happened
      </Button>
    );
  }

  if (task.client_release !== ClientRelease.ON_APPROVAL) {
    return null;
  }

  if (task.released_at) {
    return (
      <span
        className={cn("inline-flex shrink-0 items-center gap-1 text-xs font-medium text-dash-greenDeep", className)}
        data-testid={`task-client-sent-${task.id}`}
      >
        <Check className="size-3.5" aria-hidden /> Sent to client
      </span>
    );
  }

  return (
    <Button
      type="button"
      variant="dashboardOutline"
      size="sm"
      className={cn("shrink-0", className)}
      onClick={() => onRelease?.(task)}
      loading={working}
      disabled={working}
      aria-label={`Send ${task.title} to client`}
      data-testid={`task-send-client-${task.id}`}
    >
      <Send data-icon="inline-start" /> Send to client
    </Button>
  );
}
